/**
 * 全局音频状态管理器
 * 用于在浮动播放器和 AudioPlayer 组件之间同步音频状态
 */

import audioStorage from './audioStorage';

class AudioManager {
  constructor() {
    // 创建全局唯一的音频实例
    this.audio = new Audio();

    // 加载保存的偏好设置
    const preferences = audioStorage.loadPreferences();

    // 音频状态
    this.state = {
      audioData: {},
      isPlaying: false,
      currentTime: '00:00',
      totalTime: '00:00',
      progressPercentage: 0,
      currentEpisodeIndex: 0,
      volume: preferences.volume,
      volumeSlider: Math.round(preferences.volume * 100),
      lastVolume: Math.round(preferences.volume * 100),
      playMode: preferences.playMode,
      playbackRate: preferences.playbackRate,
      isDragging: false
    };

    // 监听器列表
    this.listeners = new Set();
    
    // 状态更新定时器
    this.updateTimer = null;

    // 创建节流的时间更新函数
    this.throttledTimeUpdate = audioStorage.createThrottledTimeUpdate();

    // 初始化音频事件监听
    this.initAudioEvents();

    // 初始化音频设置
    this.audio.volume = this.state.volume;
    this.audio.playbackRate = this.state.playbackRate;

    // 页面卸载时保存状态
    this.initPageUnloadHandler();
  }
  
  // 初始化音频事件监听
  initAudioEvents() {
    this.audio.addEventListener('play', () => {
      this.updateState({ isPlaying: true });
    });
    
    this.audio.addEventListener('pause', () => {
      this.updateState({ isPlaying: false });
    });
    
    this.audio.addEventListener('timeupdate', () => {
      if (!this.state.isDragging) {
        const currentTime = this.formatTime(this.audio.currentTime);
        const progressPercentage = this.audio.duration
          ? (this.audio.currentTime / this.audio.duration) * 100
          : 0;

        this.updateState({
          currentTime,
          progressPercentage
        });

        // 节流保存播放时间到本地存储
        if (this.state.audioData.id) {
          this.throttledTimeUpdate(currentTime);
        }
      }
    });
    
    this.audio.addEventListener('loadedmetadata', () => {
      const totalTime = this.formatTime(this.audio.duration);
      this.updateState({ totalTime });
    });
    
    this.audio.addEventListener('ended', () => {
      this.handleAudioEnded();
    });
    
    this.audio.addEventListener('volumechange', () => {
      this.updateState({ 
        volume: this.audio.volume,
        volumeSlider: Math.round(this.audio.volume * 100)
      });
    });
  }

  // 初始化页面卸载处理
  initPageUnloadHandler() {
    // 页面卸载时保存状态
    window.addEventListener('beforeunload', () => {
      this.saveCurrentState();
    });

    // 页面隐藏时也保存状态（移动端切换应用）
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.saveCurrentState();
      }
    });
  }

  // 保存当前状态到本地存储
  saveCurrentState() {
    if (this.state.audioData.id) {
      audioStorage.saveAudioState(this.state);

      // 保存偏好设置
      audioStorage.savePreferences({
        volume: this.state.volume,
        playMode: this.state.playMode,
        playbackRate: this.state.playbackRate
      });
    }
  }

  // 注册监听器
  subscribe(listener) {
    this.listeners.add(listener);
    
    // 返回取消订阅函数
    return () => {
      this.listeners.delete(listener);
    };
  }
  
  // 更新状态并通知所有监听器
  updateState(newState) {
    const oldState = { ...this.state };
    this.state = { ...this.state, ...newState };
    
    // 使用防抖机制减少状态通知频率
    if (this.updateTimer) {
      clearTimeout(this.updateTimer);
    }
    
    this.updateTimer = setTimeout(() => {
      // 通知所有监听器
      this.listeners.forEach(listener => {
        try {
          listener(this.state, oldState);
        } catch (error) {
          console.error('Error in audio state listener:', error);
        }
      });
    }, 16); // 约60fps的更新频率
  }
  
  // 获取当前状态
  getState() {
    return { ...this.state };
  }
  
  // 播放/暂停
  async togglePlay() {
    if (this.audio.paused) {
      try {
        await this.audio.play();
      } catch (error) {
        // 忽略 AbortError，这通常是由于快速切换导致的正常情况
        if (error.name !== 'AbortError') {
          console.error('播放失败:', error);
        }
      }
    } else {
      this.audio.pause();
    }
  }
  
  // 设置音频源
  setAudioSource(url, autoPlay = false) {
    if (this.audio.src !== url) {
      // 设置新音频源前先暂停当前播放
      this.audio.pause();
      this.audio.src = url;
      
      // 如果需要自动播放，监听音频加载完成事件
      if (autoPlay) {
        const playOnLoaded = async () => {
          try {
            await this.audio.play();
          } catch (error) {
            if (error.name !== 'AbortError') {
              console.error('自动播放失败:', error);
            }
          }
          this.audio.removeEventListener('canplay', playOnLoaded);
        };
        this.audio.addEventListener('canplay', playOnLoaded);
      }
      
      this.audio.load();
    }
  }
  
  // 设置播放时间
  setCurrentTime(time) {
    if (this.audio.duration && !isNaN(time)) {
      this.audio.currentTime = time;
    }
  }
  
  // 设置音量
  setVolume(volume) {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    this.audio.volume = clampedVolume;
    this.updateState({ 
      volume: clampedVolume,
      volumeSlider: Math.round(clampedVolume * 100)
    });
  }
  
  // 设置播放速度
  setPlaybackRate(rate) {
    this.audio.playbackRate = rate;
    this.updateState({ playbackRate: rate });
  }
  
  // 跳转到指定进度
  seekTo(percentage) {
    if (this.audio.duration) {
      const newTime = this.audio.duration * (percentage / 100);
      this.setCurrentTime(newTime);
    }
  }
  
  // 设置拖拽状态
  setDragging(isDragging) {
    this.updateState({ isDragging });
  }
  
  // 处理音频结束
  async handleAudioEnded() {
    const { playMode } = this.state;

    if (playMode === 2) {
      // 列表循环：播放下一首
      this.playNext();
    } else if (playMode === 3) {
      // 随机播放：随机选择一首
      this.playRandom();
    } else {
      // 单曲循环：重新播放当前歌曲
      this.audio.currentTime = 0;
      try {
        await this.audio.play();
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('自动播放失败:', error);
        }
      }
    }
  }

  // 播放下一首
  playNext() {
    const { currentEpisodeIndex, audioData, playMode } = this.state;
    const episodes = audioData.episodes || [];
    
    if (currentEpisodeIndex < episodes.length - 1) {
      const nextIndex = currentEpisodeIndex + 1;
      this.playEpisode(nextIndex, episodes[nextIndex].id);
    } else if (playMode === 2) {
      // 列表循环：回到第一首
      this.playEpisode(0, episodes[0].id);
    }
  }
  
  // 播放上一首
  playPrevious() {
    const { currentEpisodeIndex, audioData } = this.state;
    const episodes = audioData.episodes || [];
    
    if (currentEpisodeIndex > 0) {
      const prevIndex = currentEpisodeIndex - 1;
      this.playEpisode(prevIndex, episodes[prevIndex].id);
    }
  }
  
  // 随机播放
  playRandom() {
    const { currentEpisodeIndex, audioData } = this.state;
    const episodes = audioData.episodes || [];
    
    if (episodes.length > 1) {
      let randomIndex;
      do {
        randomIndex = Math.floor(Math.random() * episodes.length);
      } while (randomIndex === currentEpisodeIndex);
      
      this.playEpisode(randomIndex, episodes[randomIndex].id);
    }
  }
  
  // 播放指定剧集
  playEpisode(index, episodeId) {
    // 这个方法需要在组件中实现，因为需要调用 API
    // 这里只是触发事件，让组件处理
    const event = new CustomEvent('audioManager:playEpisode', {
      detail: { index, episodeId }
    });
    window.dispatchEvent(event);
  }
  
  // 切换播放模式
  togglePlayMode() {
    const newMode = this.state.playMode >= 3 ? 1 : this.state.playMode + 1;
    this.updateState({ playMode: newMode });
  }
  
  // 静音切换
  toggleMute() {
    if (this.state.volume === 0) {
      this.setVolume(this.state.lastVolume / 100);
    } else {
      this.updateState({ lastVolume: this.state.volumeSlider });
      this.setVolume(0);
    }
  }
  
  // 格式化时间
  formatTime(seconds) {
    if (!seconds || isNaN(seconds)) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  
  // 设置音频数据
  setAudioData(audioData) {
    this.updateState({ audioData });
    // 设置新音频数据时保存状态
    this.saveCurrentState();
  }

  // 设置当前剧集索引
  setCurrentEpisodeIndex(index) {
    this.updateState({ currentEpisodeIndex: index });
  }

  // 恢复保存的音频状态
  async restoreSavedState() {
    const savedState = audioStorage.loadAudioState();
    if (!savedState) {
      return null;
    }

    try {
      // 恢复基本状态
      this.updateState({
        currentEpisodeIndex: savedState.currentEpisodeIndex,
        volume: savedState.volume,
        volumeSlider: savedState.volumeSlider,
        playMode: savedState.playMode,
        playbackRate: savedState.playbackRate
      });

      // 设置音频属性
      this.audio.volume = savedState.volume;
      this.audio.playbackRate = savedState.playbackRate;

      console.log('已恢复保存的音频状态');
      return savedState;
    } catch (error) {
      console.error('恢复音频状态失败:', error);
      audioStorage.clearAudioState();
      return null;
    }
  }

  // 检查是否有保存的状态
  hasSavedState() {
    return audioStorage.hasAudioState();
  }

  // 获取保存的音频信息
  getSavedAudioInfo() {
    return audioStorage.getAudioInfo();
  }

  // 清除保存的状态
  clearSavedState() {
    audioStorage.clearAudioState();
  }

  // 销毁管理器
  destroy() {
    // 保存当前状态
    this.saveCurrentState();

    // 清理定时器
    if (this.updateTimer) {
      clearTimeout(this.updateTimer);
    }

    this.audio.pause();
    this.audio.src = '';
    this.listeners.clear();

    // 清理事件监听
    window.removeEventListener('beforeunload', this.saveCurrentState);
    document.removeEventListener('visibilitychange', this.saveCurrentState);
  }
}

// 创建全局单例
const audioManager = new AudioManager();

export default audioManager;
